// Dynamic import to handle build system compatibility
/** @type {Record<string, any> | null} */
let brandConfigs = null

/**
 * Extracts brand name from URL path
 * @returns {string | null} Brand name or null if not found
 */
export function getBrandFromUrl() {
  const path = window.location.pathname

  // Match patterns:
  // - /chat/[brand-name]/ (development)
  // - /chat/[brand-name].html (production)
  const brandMatch = path.match(/\/chat\/([^/]+?)(?:\.html|\/?)$/)

  return brandMatch?.[1] || null
}

/**
 * Loads brand configs dynamically
 * @returns {Promise<Record<string, any>>} Brand configurations
 */
async function loadBrandConfigs() {
  if (brandConfigs) return brandConfigs

  try {
    const response = await fetch('/json/brandConfigs.json')
    if (response.ok) {
      brandConfigs = await response.json()
      return brandConfigs || {}
    }
  } catch (error) {
    console.warn('Could not fetch brand configs:', error)
  }

  // Fallback to empty object
  brandConfigs = {}
  return brandConfigs
}

/**
 * Loads brand configuration for a given brand name
 * @param {string} brandName - The brand name to load config for
 * @returns {Promise<Object | null>} Brand configuration object or null if not found
 */
export async function loadBrandConfig(brandName) {
  const configs = await loadBrandConfigs()

  if (!brandName || !configs[brandName]) {
    return null
  }

  return configs[brandName]
}

/**
 * Applies brand styling to the page
 * @param {any} brandConfig - Brand configuration object
 */
export function applyBrandStyling(brandConfig) {
  const root = document.documentElement

  // Handle TurboDebt logo visibility (always run, regardless of brand config)
  const turbodebtLogo = document.getElementById('turbodebt-logo')
  if (turbodebtLogo) {
    // Hide logo when no brand config, or when hideTDLogo is explicitly true
    const shouldHide = !brandConfig || brandConfig.logo?.hideTDLogo === true
    turbodebtLogo.style.display = shouldHide ? 'none' : 'block'
  }

  if (!brandConfig) return

  // Apply CSS custom properties for theming
  Object.entries(brandConfig).forEach(([key, value]) => {
    if (key === 'logo') return // Handle logo separately

    if (typeof value === 'string') {
      // Convert camelCase to kebab-case for CSS variables
      const cssVar = key.replace(/([A-Z])/g, '-$1').toLowerCase()
      root.style.setProperty(`--brand-${cssVar}`, value)
    }
  })

  // Handle logoLayout (side-by-side layout)
  if (brandConfig.logoLayout === 'row') {
    const navContainer = document.getElementById('cobranded-nav')
    if (navContainer) {
      navContainer.style.flexDirection = 'row'
      navContainer.style.alignItems = 'center'
      navContainer.style.gap = '8px'
      navContainer.style.maxWidth = 'none'
      navContainer.style.justifyContent = 'center'
    }
  }

  // Handle cobranded logo
  if (brandConfig.logo) {
    const logoElement = /** @type {HTMLImageElement} */ (
      document.getElementById('cobranded-logo')
    )
    if (logoElement) {
      logoElement.src = brandConfig.logo.src
      logoElement.alt = 'Brand Logo'
      logoElement.style.display = 'block'

      // Set responsive logo sizing via CSS variables
      root.style.setProperty(
        '--brand-logo-desktop-width',
        brandConfig.logo.desktopWidth || '253px',
      )
      root.style.setProperty(
        '--brand-logo-mobile-width',
        brandConfig.logo.mobileWidth || '140px',
      )
    }

    // Handle mini logo
    const miniLogoElement = /** @type {HTMLImageElement} */ (
      document.getElementById('mini-logo')
    )
    if (miniLogoElement && brandConfig.logo.miniLogo) {
      miniLogoElement.src = brandConfig.logo.miniLogo.src
      miniLogoElement.alt = 'Mini Brand Logo'
      miniLogoElement.style.display = 'block'

      // Set responsive mini logo sizing via CSS variables
      root.style.setProperty(
        '--mini-logo-desktop-width',
        brandConfig.logo.miniLogo.desktopWidth || '110px',
      )
      root.style.setProperty(
        '--mini-logo-mobile-width',
        brandConfig.logo.miniLogo.mobileWidth || '72px',
      )
    } else if (miniLogoElement) {
      // Hide mini logo if not configured
      miniLogoElement.style.display = 'none'
    }
  }
}
